import React from "react";
import { Link, NavLink, useNavigate } from "react-router-dom";
import { LayoutDashboard, Building2, Images, UserCircle, Newspaper, Mail, Settings as SettingsIcon, LogOut, ExternalLink } from "lucide-react";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { to: "/admin", label: "Dashboard", icon: LayoutDashboard, end: true },
  { to: "/admin/brands", label: "Brands", icon: Building2 },
  { to: "/admin/news", label: "News", icon: Newspaper },
  { to: "/admin/media", label: "Media Library", icon: Images },
  { to: "/admin/leadership", label: "Leadership", icon: UserCircle },
  { to: "/admin/messages", label: "Messages", icon: Mail },
  { to: "/admin/settings", label: "Settings & SEO", icon: SettingsIcon },
];

export const AdminShell = ({ title, actions, children }) => {
  const { user, signOut } = useAuth();
  const navigate = useNavigate();
  const logout = () => { signOut(); navigate("/admin/login"); };

  return (
    <div className="min-h-screen lg:grid lg:grid-cols-[264px_1fr]" style={{ background: "var(--bg)" }}>
      {/* Sidebar */}
      <aside className="hidden border-r lg:flex lg:flex-col" style={{ background: "var(--surface)", borderColor: "var(--border)" }}>
        <div className="flex h-16 items-center gap-3 border-b px-5" style={{ borderColor: "var(--border)" }}>
          <img src="/brand/logo-icon.webp" alt="Acharya Holdings" className="h-8 w-8 object-contain" />
          <div>
            <p className="text-sm font-semibold leading-tight" style={{ color: "var(--text)" }}>Acharya Holdings</p>
            <p className="text-[10px] uppercase tracking-[0.2em]" style={{ color: "var(--muted)" }}>Admin</p>
          </div>
        </div>
        <nav className="flex-1 space-y-1 p-3">
          {NAV.map((n) => (
            <NavLink key={n.to} to={n.to} end={n.end} data-testid={`admin-nav-${n.label.split(" ")[0].toLowerCase()}`}
              className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm transition-colors"
              style={({ isActive }) => ({ background: isActive ? "rgba(140,106,61,0.14)" : "transparent", color: isActive ? "var(--accent-fg)" : "var(--muted)" })}>
              <n.icon size={17} /> {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="border-t p-3" style={{ borderColor: "var(--border)" }}>
          <Link to="/" target="_blank" className="flex items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm" style={{ color: "var(--muted)" }}>
            <ExternalLink size={16} /> View Website
          </Link>
          <button onClick={logout} data-testid="admin-logout-button" className="flex w-full items-center gap-3 rounded-[10px] px-3 py-2.5 text-sm" style={{ color: "var(--muted)" }}>
            <LogOut size={16} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Content */}
      <div className="flex min-h-screen flex-col">
        <header className="sticky top-0 z-30 flex h-16 items-center justify-between border-b px-4 sm:px-6" style={{ background: "var(--bg)", borderColor: "var(--border)" }}>
          <div className="flex items-center gap-3">
            <img src="/brand/logo-icon.webp" alt="" className="h-7 w-7 object-contain lg:hidden" />
            <h1 className="text-lg font-semibold tracking-[-0.01em]" style={{ color: "var(--text)" }}>{title}</h1>
          </div>
          <div className="flex items-center gap-3">
            {actions}
            <span className="hidden text-xs sm:block" style={{ color: "var(--muted)" }}>{user?.name || user?.email}</span>
            <button onClick={logout} className="inline-flex h-9 w-9 items-center justify-center rounded-md border lg:hidden" style={{ borderColor: "var(--border)", color: "var(--muted)" }} aria-label="Sign out"><LogOut size={16} /></button>
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8">{children}</main>
      </div>
    </div>
  );
};
